import React, { useState, useMemo, useCallback } from "react";
import { PencilIcon, PlusIcon } from "@heroicons/react/outline";
import Table from "components/lib/Global/ReactTable";
import TableActionMenu from "components/lib/Shared/TableActionMenu";
import Button from "components/lib/Shared/Button";
import AllotmentFormSubrow from "./AllotmentFormSubrow";

const AllotmentResultsTable = ({
  allocations,
  onDelete,
  onUpdate,
  onSubmit,
  isInReview = false,
  mode,
  isEditable = true,
  companyId,
  availableShares = [], // All shares that could be processed
  onAddAnotherClass, // Callback when "Add another class" is clicked
}) => {
  const [expandedRow, setExpandedRow] = useState({});
  const [initialRows, setInitialRows] = useState({});

  // Determine if there are remaining shares to process
  const processedShareKeys = useMemo(() => {
    if (!allocations || allocations.length === 0) return new Set();
    return new Set(
      allocations.map(
        (allocation) =>
          `${allocation.classOfShares}#${Number(allocation.sharesOnOffer)}`
      )
    );
  }, [allocations]);

  const remainingShares = useMemo(() => {
    if (!availableShares || availableShares.length === 0) return [];
    return availableShares.filter((share) => {
      const shareKey = `${share.classOfShares}#${Number(share.sharesOnOffer)}`;
      return !processedShareKeys.has(shareKey);
    });
  }, [availableShares, processedShareKeys]);

  const hasRemainingShares = remainingShares.length > 0;

  const handleAddAnotherClass = () => {
    if (onAddAnotherClass && hasRemainingShares) {
      onAddAnotherClass(remainingShares);
    }
  };

  const closeSubRow = (row) => {
    setInitialRows({});
    setExpandedRow({});
    row.toggleRowExpanded();
  };

  const handleEdit = (row, rest) => {
    if (!row.isExpanded) {
      row.toggleAllRowsExpanded(false);
      setInitialRows(rest.initialRows || {});
      setExpandedRow(row);
      row.toggleRowExpanded();
    } else {
      setInitialRows({});
      setExpandedRow({});
      row.toggleRowExpanded();
    }
  };

  const handleDelete = async (row) => {
    if (onDelete) {
      // Use the row index from the table instead of stored _index to avoid sync issues
      await onDelete(row.index);
    }
  };

  const renderRowSubComponent = useCallback(
    ({ row, rowProps, visibleColumns }) => {
      return (
        <AllotmentFormSubrow
          row={row}
          rowProps={rowProps}
          visibleColumns={visibleColumns}
          closeSubRow={() => closeSubRow(row)}
          companyId={companyId}
          onUpdate={onUpdate}
          onSubmit={onSubmit}
          mode={mode}
          allocation={row.original}
        />
      );
    },
    [companyId, onUpdate, onSubmit, mode]
  );
  // Transform allocations data for the ReactTable
  const data = useMemo(() => {
    if (!allocations || allocations.length === 0) {
      return [];
    }

    return allocations.map((allocation, index) => ({
      ...allocation,
      classOfShares: allocation.classOfShares,
      sharesOnOffer: allocation.sharesOnOffer,
      AmountCreditedAsPaidUpCapitalPerShare:
        allocation.AmountCreditedAsPaidUpCapitalPerShare ||
        allocation.paidUpShares ||
        0,
      forEvery: allocation.forEvery,
      allotAmount: allocation.allotAmount,
      remainder: allocation.remainder || 0,
    }));
  }, [allocations]);

  // Define columns for the ReactTable
  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: "Share Class",
        accessor: "classOfShares",
      },
      {
        Header: "Shares on Offer",
        accessor: "sharesOnOffer",
        Cell: ({ value }) => value?.toLocaleString(),
      },
      {
        Header: "Paid-up per Share",
        accessor: "AmountCreditedAsPaidUpCapitalPerShare",
        Cell: ({ value }) => `KES ${value || 0}`,
      },
    ];

    // Add rights issue specific columns
    if (mode === "rightsIssue") {
      baseColumns.push(
        {
          Header: "For Every",
          accessor: "forEvery",
        },
        {
          Header: "Allot Amount",
          accessor: "allotAmount",
        }
      );
    }

    // Add actions column if editable
    if (isEditable) {
      baseColumns.push({
        Header: "Actions",
        Cell: ({ row, ...rest }) => (
          <div className="flex items-center space-x-2">
            {row?.isExpanded ? (
              <button onClick={() => handleEdit(row, rest)}>Close</button>
            ) : (
              <TableActionMenu
                row={row}
                handleEdit={() => handleEdit(row, rest)}
                handleDelete={() => handleDelete(row)}
              />
            )}
          </div>
        ),
      });
    }

    return baseColumns;
  }, [mode, isEditable, handleEdit, handleDelete]);

  if (!allocations || allocations.length === 0) {
    return (
      <div className="bg-white w-full shadow p-6 text-center">
        <p className="text-gray-500">No allocations submitted yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-white w-full shadow mt-4">
      <div className="flex justify-between items-center p-3">
        <div>
          <h1 className="text-md leading-6 font-medium text-gray-900">
            Submitted {mode === "rightsIssue" ? "Rights Issue" : "Pro Rata"}{" "}
            Allocations
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {allocations.length} allocation{allocations.length !== 1 ? "s" : ""}{" "}
            submitted
            {hasRemainingShares && (
              <span className="text-sflPrimary ml-2">
                ({remainingShares.length} more available)
              </span>
            )}
          </p>
        </div>
        {hasRemainingShares && onAddAnotherClass && (
          <Button
            variant="text"
            onClick={handleAddAnotherClass}
            className="flex items-center text-sm text-sflPrimary"
          >
            <PlusIcon className="w-4 h-4 mr-1" aria-hidden="true" />
            <span>Add another Class of Shares</span>
          </Button>
        )}
      </div>
      <div className="w-full">
        <Table
          columns={columns}
          data={data}
          renderRowSubComponent={renderRowSubComponent}
        />
      </div>
    </div>
  );
};

export default AllotmentResultsTable;
